/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    ApplicationRef,
    ComponentFactoryResolver,
    ComponentRef,
    Directive,
    HostListener,
    Injector,
    Input,
    OnDestroy,
    SimpleChange,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ComponentPortal, ComponentType, DomPortalOutlet } from '@angular/cdk/portal';

@Directive()
export abstract class PrintComponent implements OnDestroy {
    protected printContent: ComponentRef<any>;

    protected stopSubject = new Subject<void>();
    protected stop$: Observable<void>;

    // inputs to be passed into the component generating the print content
    @Input() inputs: { [name: string]: any };
    @Input() disabled: boolean;

    private portalOutlet: DomPortalOutlet;

    protected abstract getPortal<T>(component: ComponentType<T>): ComponentPortal<T>;
    protected abstract getPrintData(): void;
    protected abstract initializePrintComponent(): void;
    protected abstract setInputs(): void;

    protected constructor(
        protected componentFactoryResolver: ComponentFactoryResolver,
        protected appRef: ApplicationRef,
        protected injector: Injector
    ) {
        this.stop$ = this.stopSubject.asObservable();
    }

    protected buildChange(previous, current): SimpleChange {
        let firstChange = false;
        if (typeof (previous === 'undefined')) {
            firstChange = true;
        }
        return new SimpleChange(previous, current, firstChange);
    }

    public ngOnDestroy() {
        this.stopSubject.next();
        if (this.portalOutlet) {
            this.portalOutlet.detach();
        }
    }

    @HostListener('window:afterprint')
    public afterPrint() {
        this.stopSubject.next();
        if (this.portalOutlet) {
            this.portalOutlet.detach();
        }
    }

    public print<T>(component: ComponentType<T>) {
        if (this.portalOutlet && this.portalOutlet.hasAttached()) {
            this.portalOutlet.detach();
        }
        this.createPrintPortal<T>(component);
        this.getPrintData();
    }

    private createPrintPortal<T>(component: ComponentType<T>) {
        const portal = this.getPortal<T>(component);
        this.portalOutlet = new DomPortalOutlet(
            document.querySelector('#printContent'),
            this.componentFactoryResolver,
            this.appRef,
            this.injector
        );
        this.printContent = this.portalOutlet.attachComponentPortal<T>(portal);
        this.initializePrintComponent();
    }
}
